"use client";

import React from "react";
import { Zap, CircleCheck, Loader, ShieldCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const PRODUCT_OPTIONS = ["Heavy Snoring", "Morning Fatigue", "CPAP Intolerance", "High Blood Pressure", "Restless Sleep"];
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="99309" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-accent/20 flex items-center justify-center mb-4 shadow-lg shadow-accent/10">
          <CircleCheck className="size-8 text-accent" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Assessment Submitted</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-accent/50 hover:bg-accent/5 text-accent">
          Check Another Profile
        </Button>
      </div>;
  }
  const inputBaseClasses = "flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200";
  return <Form onSubmit={handleSubmit} className="space-y-6 mt-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Full Name</FormLabel>
              <FormControl>
                <input type="text" placeholder="Jane Smith" className={`${inputBaseClasses} h-10`} {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Enter your legal name for clinical records.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Email Address</FormLabel>
              <FormControl>
                <input type="email" placeholder="jane@example.com" className={`${inputBaseClasses} h-10`} {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">We will send your candidacy results here.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Phone Number</FormLabel>
              <FormControl>
                <input type="tel" placeholder="(555) 000-0000" className={`${inputBaseClasses} h-10`} {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">For scheduling your free consultation.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_contact_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Preferred Consultation Date</FormLabel>
              <FormControl>
                <input type="date" className={`${inputBaseClasses} h-10`} {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Select a day that works best for you.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="product_type" rules={{
      required: "Please select your primary concern"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Primary Sleep Concern</FormLabel>
            <FormControl>
              <select className={`${inputBaseClasses} h-10 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23888%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:1em_1em] bg-[right_0.75rem_center] bg-no-repeat pr-10`} {...field}>
                <option value="" disabled className="bg-background text-muted-foreground">Select a concern</option>
                {PRODUCT_OPTIONS.map((opt, i) => <option key={opt} value={opt} data-index={i} className="bg-background text-foreground">
                    {opt}
                  </option>)}
              </select>
            </FormControl>
            <FormDescription className="text-xs opacity-70">Choose the symptom that affects you most.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide symptom details"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Symptoms & Health History</FormLabel>
            <FormControl>
              <textarea placeholder="Describe your snoring, fatigue level, or previous treatments..." className={`${inputBaseClasses} min-h-[120px] resize-y`} {...field} />
            </FormControl>
            <FormDescription className="text-xs opacity-70">Include any information about CPAP usage or insurance providers.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <ShieldCheck className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold bg-accent text-white hover:bg-accent/90 shadow-md transition-all duration-300 border-none group relative overflow-hidden rounded-lg">
        <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
        <span className="relative flex items-center justify-center gap-2">
          {isSubmitting ? <>
              <Loader className="animate-spin size-5" />
              Analyzing Data...
            </> : <>
              <Zap className="size-5 fill-white/50" />
              Check My Candidacy
            </>}
        </span>
      </Button>
    </Form>;
}
export default function ExpertConsultation() {
  return <section id="check-your-candidacy" className="bg-background text-foreground py-24 px-4 relative overflow-hidden min-h-screen flex items-center justify-center">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%)] opacity-5 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-30" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-30" />
      
      {}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-3xl w-full mx-auto relative z-10">
        <div className="text-center mb-10 space-y-4">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
            Check Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-600">Candidacy</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Find out if Oral Appliance Therapy is the right solution for your sleep apnea and health goals.
          </p>
        </div>

        <FormProvider formKey="check_candidacy_form_oral_appliances" formId="check_candidacy_form_oral_appliances" sectionName="CandidacyCheck" defaultValues={{
        name: "",
        email: "",
        phone: "",
        product_type: "",
        preferred_contact_date: "",
        message: ""
      }} submitText="Check My Candidacy" successMessage="Your assessment has been securely received. Our sleep specialists will review your details and contact you shortly.">
          <Card className="bg-card/60 backdrop-blur-md border border-border shadow-xl overflow-hidden relative rounded-xl">
            {}
            <div className="absolute inset-0 shadow-[inset_0_0_40px_rgba(0,0,0,0.02)] pointer-events-none" />
            
            <CardHeader className="pb-4 border-b border-border bg-muted/30">
              <CardTitle className="text-2xl flex items-center gap-2 text-primary">
                <Zap className="size-6 text-primary" />
                Symptom Assessment
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground">
                Provide details about your sleep health for a tailored medical recommendation.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-6">
              <Tabs defaultValue="home" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8 bg-muted p-1 border border-border rounded-lg">
                  <TabsTrigger value="home" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
                    Patient Assessment
                  </TabsTrigger>
                  <TabsTrigger value="commercial" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
                    Clinical Referral
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="home" className="mt-0 outline-none">
                  <ConsultationFormContent />
                </TabsContent>
                
                <TabsContent value="commercial" className="mt-0 outline-none">
                  <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-md">
                    <p className="text-sm text-primary flex items-center gap-2">
                      <ShieldCheck className="size-4 text-primary" />
                      Providers: Use this form to refer a patient for specialized oral appliance therapy.
                    </p>
                  </div>
                  <ConsultationFormContent />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </FormProvider>
      </div>
    </section>;
}